import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { ArrowRight } from "lucide-react";
import yapheneImg from "@/assets/yaphene-hair-care-display.jpg";
import marapaImg from "@/assets/marapa-routine-lifestyle.jpg";
import nezerImg from "@/assets/nezer-full-lineup.jpg";
import calumeImg from "@/assets/calume-reed-diffusers.jpg";
import herbzImg from "@/assets/herbz-herbal-balms.jpg";
import flaurielImg from "@/assets/flauriel-hot-sauces.jpg";
import tasteImg from "@/assets/flauriel-taste-st-kitts.jpg";
import discoverImg from "@/assets/discover/discover-hero.jpg";
import anaelliImg from "@/assets/anaelli-caribbean-essence-notebook.jpg";
import chlmImg from "@/assets/chlm-hero.jpg";
import libraryImg from "@/assets/discover/island-library-hero.jpg";
import balmBondImg from "@/assets/balm-bond-workshop-4.jpg";
import sunriseImg from "@/assets/sunrise-stillness-hero.jpg";
import campImg from "@/assets/camp-group-pose.jpg";
import workshopImg from "@/assets/balm-bond-workshop-1.jpg";
import tallivoImg from "@/assets/tallivo-hero-header.jpg";
import tallivoPosImg from "@/assets/tallivo-pos-overview.png";
import rootedRitualImg from "@/assets/forest/rooted-ritual-hero.jpg";
import natureTeacherImg from "@/assets/forest/living-forest-section.jpg.asset.json";

type Member = { name: string; image: string; to: string };
type World = {
  number: string;
  eyebrow: string;
  title: string;
  description: string;
  to: string;
  members: Member[];
  tone?: "warm" | "quiet";
};

const WORLDS: World[] = [
  {
    number: "I",
    eyebrow: "World One",
    title: "Wellness & Beauty",
    description:
      "Botanical hair care, skin rituals, men's wellness and atmospheric scent — handcrafted in St. Kitts from heritage plants.",
    to: "/shop",
    members: [
      { name: "Yaphene", image: yapheneImg, to: "/brands/yaphene" },
      { name: "Marapa", image: marapaImg, to: "/brands/marapa" },
      { name: "Nezer", image: nezerImg, to: "/brands/nezer" },
      { name: "Herbz Kulture", image: herbzImg, to: "/brands/herbz-kulture" },
      { name: "Calumé", image: calumeImg, to: "/brands/calume" },
    ],
  },
  {
    number: "II",
    eyebrow: "World Two",
    title: "Culture & Taste",
    description:
      "Caribbean foods, sauces, traditional sweets, and the living food memory of St. Kitts.",
    to: "/taste-st-kitts",
    members: [
      { name: "Flauriel Foods", image: flaurielImg, to: "/brands/flauriel" },
      { name: "Taste St. Kitts", image: tasteImg, to: "/taste-st-kitts" },
      { name: "Discover St. Kitts", image: discoverImg, to: "/discover-st-kitts" },
    ],
  },
  {
    number: "III",
    eyebrow: "World Three",
    title: "Learning & Reflection",
    description:
      "Journals, wellness guides and the Caribbean Longevity Method — slow knowledge for modern living.",
    to: "/island-library",
    members: [
      { name: "AnaElli Digital", image: anaelliImg, to: "/brands/anaelli" },
      { name: "Longevity Method", image: chlmImg, to: "/caribbean-heritage-longevity-method" },
      { name: "Island Library", image: libraryImg, to: "/island-library" },
    ],
  },
  {
    number: "IV",
    eyebrow: "World Four",
    title: "Experiences & Retreats",
    description:
      "Botanical workshops, restorative retreats and creative gatherings rooted in island place.",
    to: "/experiences/balm-and-bond",
    members: [
      { name: "Balm & Bond", image: balmBondImg, to: "/experiences/balm-and-bond" },
      { name: "Sunrise & Stillness", image: sunriseImg, to: "/experiences/sunrise-and-stillness" },
      { name: "Creative Immersion", image: campImg, to: "/experiences/creative-immersion" },
      { name: "Workshops", image: workshopImg, to: "/events" },
    ],
  },
  {
    number: "V",
    eyebrow: "World Five",
    title: "Tools for Building",
    description:
      "Quiet, practical tools for Caribbean entrepreneurs — point-of-sale, financial signals, and steady commerce.",
    to: "/brands/tallivo",
    tone: "quiet",
    members: [
      { name: "Tallivo App", image: tallivoImg, to: "/brands/tallivo" },
      { name: "Tallivo POS", image: tallivoPosImg, to: "/brands/tallivo" },
    ],
  },
  {
    number: "VI",
    eyebrow: "World Six",
    title: "Rooted Ritual",
    description:
      "A digital sanctuary for reflection, healing, and personal growth. Guided rituals, original music, journaling journeys, and wellness experiences inspired by nature, memory, and Caribbean traditions.",
    to: "/rooted-ritual",
    members: [
      { name: "Rooted Ritual", image: rootedRitualImg, to: "/rooted-ritual" },
      { name: "Nature as Teacher", image: natureTeacherImg.url, to: "/nature-as-teacher" },
    ],
  },
];

const EcosystemWorlds = () => {
  return (
    <section className="py-24 md:py-32 bg-background">
      <div className="container mx-auto px-6" style={{ maxWidth: "1400px" }}>
        <Reveal className="max-w-2xl mb-16 md:mb-24">
          <p className="font-sans text-[11px] tracking-[0.42em] uppercase text-accent mb-5">
            The Ecosystem
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-foreground leading-[1.05] mb-6">
            Worlds Within the House
          </h2>
          <p className="font-sans text-base md:text-lg text-foreground/70 leading-relaxed">
            Sugar Town Organics is a living ecosystem of brands, experiences and
            ideas. Each world holds its own atmosphere — together, they shape a
            way of living Caribbean.
          </p>
        </Reveal>

        <div className="space-y-24 md:space-y-32">
          {WORLDS.map((world, wIdx) => {
            const isQuiet = world.tone === "quiet";
            const reversed = wIdx % 2 === 1;
            return (
              <Reveal key={world.title}>
                <div
                  className={`grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start ${
                    isQuiet ? "opacity-90" : ""
                  }`}
                >
                  <div className={reversed ? "lg:order-2" : ""}>
                    <p className="font-serif italic text-accent text-lg mb-3">
                      {world.number} · <span className="font-sans not-italic text-[11px] tracking-[0.32em] uppercase">{world.eyebrow}</span>
                    </p>
                    <h3 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-foreground leading-[1.05] mb-5">
                      {world.title}
                    </h3>
                    <p className="font-sans text-foreground/70 leading-relaxed mb-7 max-w-md">
                      {world.description}
                    </p>
                    <Link
                      to={world.to}
                      className="group inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.32em] uppercase text-foreground hover:text-accent transition-colors"
                    >
                      Enter World
                      <span className="relative inline-block w-10 h-px bg-accent overflow-hidden">
                        <span className="absolute inset-y-0 right-0 w-2 h-px bg-accent rotate-45 origin-right -translate-y-[3px]" />
                      </span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>

                  <div
                    className={`grid grid-cols-2 ${
                      world.members.length >= 4 ? "md:grid-cols-4" : "md:grid-cols-3"
                    } gap-3 md:gap-4 ${reversed ? "lg:order-1" : ""}`}
                  >
                    {world.members.map((m) => (
                      <Link
                        key={m.name}
                        to={m.to}
                        className="group relative block aspect-[3/4] overflow-hidden rounded-sm bg-[hsl(150_35%_8%)]"
                      >
                        <img
                          src={m.image}
                          alt={m.name}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-75 group-hover:scale-[1.05] transition-all duration-[900ms] ease-out"
                        />
                        <div
                          aria-hidden
                          className="absolute inset-0 bg-gradient-to-t from-[hsl(150_35%_8%)]/85 via-[hsl(150_35%_8%)]/15 to-transparent"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-4">
                          <p className="font-serif text-[hsl(40_33%_96%)] text-base md:text-lg leading-tight group-hover:text-accent transition-colors">
                            {m.name}
                          </p>
                          <span className="mt-2 inline-block h-px w-6 bg-accent/70 group-hover:w-14 transition-all duration-500" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EcosystemWorlds;
